// Constants for frequency range (matching QuantumEngine.js)
const MIN_FREQ = 1e-6;    // 1 µHz (micro-hertz) - Covers extremely low frequency waves
const MAX_FREQ = 1e28;    // 10²⁸ Hz - Covers highest energy gamma rays and beyond

// Dynamic frequency bands based on quantum engine configuration
const generateBands = () => [
  { unit: "µHz", exp: -6, range: [1e-6, 1e-3] },      // Micro Hz
  { unit: "mHz", exp: -3, range: [1e-3, 1] },         // Milli Hz
  { unit: "Hz", exp: 0, range: [1, 1e3] },            // Hertz
  { unit: "kHz", exp: 3, range: [1e3, 1e6] },         // Kilohertz
  { unit: "MHz", exp: 6, range: [1e6, 1e9] },         // Megahertz
  { unit: "GHz", exp: 9, range: [1e9, 1e12] },        // Gigahertz
  { unit: "THz", exp: 12, range: [1e12, 1e15] },      // Terahertz
  { unit: "PHz", exp: 15, range: [1e15, 1e18] },      // Petahertz
  { unit: "EHz", exp: 18, range: [1e18, 1e21] },      // Exahertz
  { unit: "ZHz", exp: 21, range: [1e21, 1e24] },      // Zettahertz
  { unit: "YHz", exp: 24, range: [1e24, 1e28] }       // Yottahertz+
];

// Get current bands
const BANDS = generateBands();

// Map frequency ranges to electromagnetic spectrum bands with green aesthetic
const SPECTRUM_BANDS = [
  { name: "ELF", range: [1e-6, 1e0], color: [0, 100, 0] },      // Dark green
  { name: "Radio", range: [1e0, 1e12], color: [0, 130, 0] },    // Deep green
  { name: "Terahertz", range: [1e12, 1e14], color: [0, 160, 0] },// Medium green
  { name: "Infrared", range: [1e14, 1e15], color: [0, 190, 0] }, // Bright green
  { name: "Visible", range: [1e15, 1e15 * 0.789], color: [0, 220, 0] }, // Lighter green
  { name: "UV", range: [1e15 * 0.789, 1e17], color: [0, 255, 0] }, // Pure green
  { name: "X-Ray", range: [1e17, 1e20], color: [100, 255, 100] }, // Light green
  { name: "Gamma", range: [1e20, 1e28], color: [180, 255, 180] }  // Very light green
];

// Calculate band positions based on logarithmic scale
const getBandPosition = (freq, width, padding) => {
  const logMin = Math.log(MIN_FREQ);
  const logMax = Math.log(MAX_FREQ);
  const logFreq = Math.log(freq);
  return padding + (width - 2 * padding) * (logFreq - logMin) / (logMax - logMin);
};

const FrequencyScale = (p5, x, y, w, h, frequency, minFreq, maxFreq, currentState, detectedFreq) => {
  const padding = w * 0.15; // 15% padding on each side
  
  p5.textFont('IBM BIOS');
  p5.textAlign(p5.CENTER, p5.BOTTOM);
  p5.textSize(10);

  // Draw spectrum bands with enhanced gradient
  const gradientSteps = 200; // Increased for smoother transitions
  for (let i = 0; i < gradientSteps; i++) {
    const pos = i / gradientSteps;
    const xPos = x + pos * w;
    const width = w / gradientSteps;
    
    // Calculate frequency at this position
    const logPos = pos * (Math.log(MAX_FREQ) - Math.log(MIN_FREQ)) + Math.log(MIN_FREQ);
    const freq = Math.exp(logPos);
    
    // Find current spectrum band
    const spectrumBand = SPECTRUM_BANDS.find(b => freq >= b.range[0] && freq <= b.range[1]);
    
    if (spectrumBand) {
      // Calculate position within the band for color interpolation
      const bandLogMin = Math.log(spectrumBand.range[0]);
      const bandLogMax = Math.log(spectrumBand.range[1]);
      const bandPos = (Math.log(freq) - bandLogMin) / (bandLogMax - bandLogMin);
      
      // Get next band for color interpolation
      const nextBand = SPECTRUM_BANDS[SPECTRUM_BANDS.indexOf(spectrumBand) + 1];
      
      let r, g, b;
      if (nextBand && bandPos > 0.8) {
        // Interpolate to next band color for smooth transitions
        const t = (bandPos - 0.8) * 5; // Scale 0.8-1.0 to 0-1
        r = p5.lerp(spectrumBand.color[0], nextBand.color[0], t);
        g = p5.lerp(spectrumBand.color[1], nextBand.color[1], t);
        b = p5.lerp(spectrumBand.color[2], nextBand.color[2], t);
      } else {
        [r, g, b] = spectrumBand.color;
      }
      
      // Draw gradient segment with spectrum color
      const alpha = p5.map(Math.sin(bandPos * Math.PI), -1, 1, 100, 200);
      p5.noStroke();
      p5.fill(r, g, b, alpha);
      p5.rect(xPos, y + h - 5, width + 1, 5);
      
      // Add band indicators
      if (bandPos < 0.02 || bandPos > 0.98) {
        p5.fill(255, 255, 255, 100);
        p5.rect(xPos, y + h - 7, width + 1, 7);
      }
    }
  }
  
  // Draw scale border
  p5.stroke(0, 255, 0, 150);
  p5.strokeWeight(1);
  p5.noFill();
  p5.rect(x, y + h - 5, w, 5);
  
  // Draw major band separators and labels
  BANDS.forEach((band, i) => {
    // Draw band boundaries
    const bandStartX = x + getBandPosition(band.range[0], w, padding);
    const bandEndX = x + getBandPosition(band.range[1], w, padding);
    
    // Draw band separator
    p5.stroke(0, 255, 0, 100);
    p5.line(bandStartX, y, bandStartX, y + h);
    
    // Draw band label with range
    const bandCenterX = (bandStartX + bandEndX) / 2;
    p5.noStroke();
    p5.fill(0, 255, 0, 150);
    p5.text(band.unit, bandCenterX, y - 15);
    
    // Draw range values in scientific notation
    p5.textSize(8);
    const startExp = Math.floor(Math.log10(band.range[0]));
    const endExp = Math.floor(Math.log10(band.range[1]));
    p5.text(`10^${startExp}`, bandStartX, y - 2);
    if (i === BANDS.length - 1) {
      p5.text(`10^${endExp}`, bandEndX - 20, y - 2);
    }
    
    // Draw logarithmic tick marks with values
    const majorTicks = 5;
    for (let j = 0; j <= majorTicks; j++) {
      const logPos = j / majorTicks;
      const tickFreq = band.range[0] * Math.pow(band.range[1]/band.range[0], logPos);
      const tickX = x + getBandPosition(tickFreq, w, padding);
      
      // Draw major tick
      p5.stroke(0, 255, 0, 100);
      p5.line(tickX, y + h - 10, tickX, y + h);
      
      // Draw minor ticks between major ticks
      if (j < majorTicks) {
        const nextTickFreq = band.range[0] * Math.pow(band.range[1]/band.range[0], (j + 1) / majorTicks);
        for (let k = 1; k < 4; k++) {
          const minorLogPos = j / majorTicks + (k / 4) * (1 / majorTicks);
          const minorFreq = band.range[0] * Math.pow(band.range[1]/band.range[0], minorLogPos);
          const minorX = x + getBandPosition(minorFreq, w, padding);
          p5.stroke(0, 255, 0, 50);
          p5.line(minorX, y + h - 5, minorX, y + h);
        }
      }
    }
  });
  
  // Draw scanning frequency marker at exact frequency position
  const freqPos = x + getBandPosition(frequency, w, padding);
  p5.stroke(0, 255, 0, 150);
  p5.strokeWeight(2);
  const markerHeight = h - 10;
  
  // Calculate current frequency band
  const currentBand = BANDS.find(band => frequency >= band.range[0] && frequency <= band.range[1]) || BANDS[0];
  
  // Calculate marker width based on frequency range
  const bandWidth = Math.max(2,
    (getBandPosition(currentBand.range[1], w, padding) -
     getBandPosition(currentBand.range[0], w, padding)) / 40
  );
  
  // Draw scanning beam with basic effect
  const beamCount = 7;
  
  // Draw frequency label during scanning
  if (currentState === 'SCANNING') {
    p5.textAlign(p5.CENTER, p5.BOTTOM);
    p5.textSize(10);
    p5.fill(0, 255, 0, 150);
    p5.text(formatFrequency(frequency), freqPos, y - 5);
  }
  
  // Draw scanning beam
  for (let i = 0; i < beamCount; i++) {
    const alpha = p5.map(i, 0, beamCount, 200, 0);
    const jitter = Math.sin(p5.frameCount * 0.1 + i) * bandWidth * 0.2;
    p5.stroke(0, 255, 0, alpha);
    p5.line(freqPos - i + jitter, y + 5, freqPos - i, y + markerHeight);
    p5.line(freqPos + i + jitter, y + 5, freqPos + i, y + markerHeight);
  }
  
  // If signal is detected and we have a valid frequency, draw a persistent marker
  if ((currentState === 'SIGNAL_FOUND' || currentState === 'DISPLAYING_CALL' || currentState === 'ANALYZING_SIGNAL') && detectedFreq !== null && detectedFreq > 0) {
    const detectedPos = x + getBandPosition(detectedFreq, w, padding);
    
    // Get detected frequency band info
    const detectedBand = BANDS.find(band => detectedFreq >= band.range[0] && detectedFreq <= band.range[1]);
    const bandWidth = detectedBand ?
      (getBandPosition(detectedBand.range[1], w, padding) - getBandPosition(detectedBand.range[0], w, padding)) / 20 :
      3;
    
    // Draw pulsing glow effect
    const pulseIntensity = (Math.sin(p5.frameCount * 0.1) + 1) * 0.5;
    const glowWidth = bandWidth * 6;
    
    // Draw detection glow
    for (let i = glowWidth; i > 0; i--) {
      const alpha = p5.map(i, glowWidth, 0, 255 * pulseIntensity, 0);
      p5.stroke(255, 50, 0, alpha);
      p5.strokeWeight(1);
      p5.line(detectedPos - i, y, detectedPos - i, y + h);
      p5.line(detectedPos + i, y, detectedPos + i, y + h);
    }
    
    // Draw main detection line
    p5.stroke(255, 50, 0, 200);
    p5.strokeWeight(3);
    p5.line(detectedPos, y, detectedPos, y + h);
    
    // Draw detection indicator
    p5.noFill();
    const pulseSize = bandWidth * 4 + Math.sin(p5.frameCount * 0.1) * bandWidth;
    
    for (let i = 0; i < 3; i++) {
      const size = pulseSize + (i * bandWidth);
      const alpha = p5.map(i, 0, 3, 200, 50);
      p5.stroke(255, 50, 0, alpha);
      p5.ellipse(detectedPos, y + h/2, size, size);
    }
    
    // Display detected frequency value
    p5.textAlign(p5.CENTER, p5.BOTTOM);
    p5.textSize(10);
    p5.fill(255, 50, 0, 200);
    const freqLabel = formatFrequency(detectedFreq);
    p5.text(freqLabel, detectedPos, y - 5);
  }
  
  p5.strokeWeight(1); // Reset stroke weight
};

// Helper function to format frequency with spectrum band info
const formatFrequency = (freq) => {
  // Find the appropriate frequency band and spectrum band
  const band = BANDS.find(b => freq >= b.range[0] && freq <= b.range[1]);
  const spectrumBand = SPECTRUM_BANDS.find(b => freq >= b.range[0] && freq <= b.range[1]);
  
  let formattedValue = '';
  
  if (band) {
    // Convert frequency to appropriate unit
    const scale = Math.pow(10, band.exp);
    const value = freq / scale;
    
    // Determine precision based on magnitude and band
    const magnitude = Math.floor(Math.log10(freq));
    const precision = magnitude < 0 ? 6 :
                     magnitude < 3 ? 3 :
                     magnitude > 20 ? 0 :
                     1;
    
    formattedValue = `${value.toFixed(precision)} ${band.unit}`;
  } else {
    // Handle frequencies outside defined bands
    if (freq < MIN_FREQ) {
      formattedValue = `${(freq * 1e6).toFixed(6)} µHz`;
    } else if (freq > MAX_FREQ) {
      formattedValue = `${(freq / 1e24).toFixed(0)} YHz+`;
    } else {
      formattedValue = `${freq.toExponential(2)} Hz`;
    }
  }
  
  return formattedValue;
};

export default FrequencyScale;